import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import Button from '@ui-library/button';
import ResourcesView from '@components/sources/source-view/source-resources/resources-view';
import ResourcesEdit from '@components/sources/source-view/source-resources/resources-edit';
import useModalView from '@hooks/useModalView';
import ConfirmChanges from '@ui-library/modal/confirmChanges';
import useStores from '@stores/useStores';
import sourceFactory from '@components/sources/sourceFactory';
import { SourceConfig } from '@stores/source';
import classes from './source-resources.module.scss';

const UPDATE_MODAL_TEXT =
  'These changes might affect the way your pipeline and ' +
  'destination schema operates. Please confirm that you want these changes to ' +
  'be committed.';
const CANCEL_MODAL_TEXT =
  'You’re about to leave this page with unsaved changes. Are you sure you want to continue?';

type Props = { isAdmin: boolean };

export default observer(({ isAdmin }: Props) => {
  const { sourceDataStore } = useStores();
  const [show, setShow] = useState(true);
  const [edit, setEdit] = useState(false);

  const [config, setConfig] = useState<SourceConfig | undefined>({});
  const [loading, setLoading] = useState(false);

  const handleEditClick = useCallback(() => {
    setEdit(true);
    setConfig({});
  }, [setEdit, setConfig]);

  const onCancel = useCallback(() => {
    setEdit(false);
    setConfig(undefined);
  }, [setConfig, setEdit]);

  const handleChange = useCallback(
    (resourcesConfig: SourceConfig) => {
      setConfig(resourcesConfig);
    },
    [setConfig],
  );

  const onSubmit = useCallback(async () => {
    if (
      !(
        config!.resources?.length ||
        config!.customResources?.length ||
        config?.catalog?.streams?.length
      ) ||
      loading
    ) {
      return;
    }
    setLoading(true);
    const data = sourceFactory(sourceDataStore!.sourceDef.category)?.transformResourcesConfig(
      config!,
    );
    await sourceDataStore.updateConfig(data);
    setEdit(false);
    setLoading(false);
    // handle save
  }, [setLoading, config, sourceDataStore.updateConfig, setEdit]);

  const [updateModal, showUpdateModal] = useModalView(
    <ConfirmChanges
      onConfirm={onSubmit}
      onCancel={() => showUpdateModal(false)}
      type="save"
      text={UPDATE_MODAL_TEXT}
    />,
  );
  const [cancelModal, showCancelModal] = useModalView(
    <ConfirmChanges
      onConfirm={onCancel}
      onCancel={() => showCancelModal(false)}
      type="cancel"
      text={CANCEL_MODAL_TEXT}
    />,
  );
  return (
    <div>
      {updateModal}
      {cancelModal}
      <div className={classes.header}>
        <div className={classes.header_info}>
          <div className={classes.header_info__main_text}>
            {edit ? 'Update ' : ''}
            Resources
          </div>
        </div>
        <div className={classes.header_cta}>
          {isAdmin && !edit && show && <Button secondary onClick={handleEditClick} name="Update" />}
          {edit && (
            <>
              <Button secondary onClick={() => showCancelModal(true)} name="Cancel" />
              <Button
                className={classes.header_cta__save}
                onClick={() => showUpdateModal(true)}
                disabled={
                  !(
                    config?.resources?.length ||
                    config?.customResources?.length ||
                    config?.catalog?.streams?.length
                  )
                }
                loading={loading}
                name="Save"
              />
            </>
          )}
        </div>
      </div>
      <div className={`${classes.resource_data_container} ${!edit ? classes.view_mode : ''}`}>
        {!show && <div>No Resources</div>}
        {show && edit ? (
          <ResourcesEdit source={sourceDataStore} onChange={handleChange} />
        ) : (
          <ResourcesView source={sourceDataStore} onHideResources={() => setShow(false)} />
        )}
      </div>
    </div>
  );
});
